/**
 * @file Sticky ATC bar (mobile PDP) — CNV-5116
 *
 * Bottom-fixed bar that appears once the buy box's own add-to-cart control has
 * scrolled off the top of the viewport. Collapsed it is a title and an
 * ADD TO ORDER button; tapping that expands a sheet carrying the buy box's own
 * choices — sizes, plan, delivery frequency — and a priced ADD TO CART.
 *
 * The sheet owns no state. Every control forwards to the real control in the
 * buy box and the sheet re-renders from the `product-form-update` that comes
 * back, so it cannot drift from the buy box, and Skio's selling_plan, UpCart
 * and TriplePixel keep the single path they already have:
 *
 *   size pill        -> input.variant-option-input          .click()
 *   plan row         -> input[name="purchase_type"]         .click()
 *   delivery select  -> select[id$="-sellingPlanOptions"]   value + change
 *   add to cart      -> button[name="add"]                  .click()
 *
 * The frequency select is set and told to fire `change` rather than writing the
 * hidden selling_plan input directly: that input is owned by
 * purchase-type-selector, and direct writes to it go stale.
 *
 * Two buy-box families, one interface:
 *
 *   adapter="product-form"  pdp-hero-v2 / -v3 / -gifts / -kalejunkie
 *                           state: `product-form-update` (product-form.js)
 *   adapter="ctr"           ctr-stack-save-buy-box (no sheet — nothing in the
 *                           ladder's flavour/stepper UI maps onto size + plan)
 *                           state: `ctr:buy-box-update` (ctr-buy-box-ladder.js)
 *
 * Deliberately NOT reusing `.rtnu-product__form-submit` or `[data-variant-badge]`:
 * the buy box sections query both document-wide, so a second set outside the
 * form would silently steal their handlers.
 */

/**
 * Same shape as assets/ctr-buy-box-ladder.js's money(), and for the same
 * reason: product-form-utils' formatMoney() is .toFixed(0) and drops the cents
 * the design asks for.
 *
 * The difference is the symbol. Both of those helpers hardcode '$', which is
 * why a market on another currency renders ₹4,800 as "$4800" in the buy box
 * today. Rather than repeat that here, the bar reads its own first paint —
 * rendered by Liquid's `money` filter — and keeps whatever wraps the digits.
 */
function readMoneyShape(rendered) {
  const shape = { prefix: '$', suffix: '' };
  if (!rendered) return shape;

  const match = rendered.match(/^(\D*)[\d.,\s]+(\D*)$/);
  if (!match) return shape;

  shape.prefix = match[1] || '';
  shape.suffix = match[2] || '';
  return shape;
}

function money(cents, shape) {
  const safe = Number.isFinite(cents) ? cents : 0;
  const { prefix, suffix } = shape || { prefix: '$', suffix: '' };
  // Grouped, unlike the two helpers this borrows from. The first paint comes
  // from Liquid's `money`, which groups — without this the price visibly jumps
  // from "Rs. 4,320.00" to "Rs. 8280.00" on the first interaction.
  const amount = (safe / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${prefix}${amount}${suffix}`;
}

/** An element that is display:none has no box — treat it as absent. */
function isRendered(el) {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 || rect.height > 0;
}

/**
 * Intelligems' Events Manager assigns each Custom JavaScript Event an opaque
 * generated identifier when it's created there — the theme can't pick its own
 * event name, so these have to match whatever the dashboard generated exactly.
 * Named here for clicks/ATC and subscription
 * attachment straight from the sticky bar.
 */
const IG_EVENT_STICKY_ATC_CLICK = 'javascripteventOTVIM';
const IG_EVENT_STICKY_ATC_SUBSCRIPTION_ATTACH = 'javascripteventRJNCZ';

class StickyAtcBar extends HTMLElement {
  connectedCallback() {
    // One bar per page. A template with two buy box sections enabled would
    // otherwise stack them.
    if (document.querySelector('sticky-atc-bar[data-active]') !== null) {
      this.remove();
      return;
    }
    this.setAttribute('data-active', '');

    this.adapter = this.dataset.adapter === 'ctr' ? 'ctr' : 'product-form';
    this.hasChoices = this.dataset.hasChoices === 'true';
    this.scope = document.getElementById(this.dataset.scope);

    this.panel = this.querySelector('[data-sticky-panel]');
    this.body = this.querySelector('[data-sticky-body]');
    this.toggle = this.querySelector('[data-sticky-toggle]');
    this.cta = this.querySelector('[data-sticky-submit]');
    this.labelEl = this.querySelector('[data-sticky-label]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.moneyShape = readMoneyShape(this.dataset.moneySample);
    this.compareEl = this.querySelector('[data-sticky-compare]');
    this.subPriceEl = this.querySelector('[data-sticky-sub-price]');
    this.subCompareEl = this.querySelector('[data-sticky-sub-compare]');
    this.onetimePriceEl = this.querySelector('[data-sticky-onetime-price]');
    this.freqRoot = this.querySelector('[data-sticky-freq]');
    this.freqButton = this.querySelector('[data-sticky-freq-button]');
    this.freqValueEl = this.querySelector('[data-sticky-freq-value]');
    this.freqList = this.querySelector('[data-sticky-freq-list]');
    this.freqOptions = Array.from(this.querySelectorAll('[data-sticky-freq-option]'));
    this.dividerEl = this.querySelector('[data-sticky-divider]');
    this.dashEl = this.querySelector('[data-sticky-dash]');
    this.scrimEl = this.querySelector('[data-sticky-scrim]');

    if (!this.scope || !this.cta) {
      this.remove();
      return;
    }

    this.control = this.findControl();
    if (!this.control) {
      // Sold out: the buy box renders a back-in-stock surface instead of an
      // add-to-cart control, and that surface is not duplicated here.
      this.remove();
      return;
    }

    this.bindControls();
    this.watchScroll();
    this.watchState();
    this.syncDisabled();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.controlObserver) this.controlObserver.disconnect();
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    if (this.onDocumentPointerDown) {
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
    }
  }

  /**
   * The buy box's own add-to-cart control — also the scroll trigger.
   * `scope` is the <form> itself for product-form, the <ctr-buy-box> for ctr,
   * so a bare descendant selector is already scoped to this buy box.
   *
   * "First match" is not enough. On a product with no Skio plan, pdp-hero-v2
   * renders BOTH add-to-cart buttons — the subscription one inside
   * `.lp-offer-gated`, which is hidden, and the plain one in
   * `.m-atc-btn`, which is the live control. Take the one that has a box.
   */
  findControl() {
    const selector = this.adapter === 'ctr' ? '[data-ctr-submit]' : 'button[name="add"]';
    const candidates = Array.from(this.scope.querySelectorAll(selector));
    return candidates.find(isRendered) || candidates[0] || null;
  }

  /**
   * Re-point at the live control when the cached one loses its box — a gate
   * flipping, or the sold-out swap hiding it. Returns false when nothing in
   * the buy box is offering an add-to-cart right now, which is the bar's cue
   * to stay down.
   */
  refreshControl() {
    if (isRendered(this.control)) return true;

    const next = this.findControl();
    if (!next || next === this.control || !isRendered(next)) return false;

    if (this.observer) this.observer.disconnect();
    if (this.controlObserver) this.controlObserver.disconnect();
    this.control = next;
    this.observeControl();
    return true;
  }

  /* -------------------------------------------------------------- tracking */

  /**
   * Intelligems' custom-event queue — a dataLayer-style array its app embed
   * drains once it loads, so a push before that script is ready is never
   * lost. `event` must be one of the IG_EVENT_* identifiers above, each
   * pre-created as a Custom JavaScript Event in Intelligems' Events Manager.
   */
  trackIgEvent(event, properties) {
    window.igEvents = window.igEvents || [];
    window.igEvents.push({ event, properties });
  }

  /**
   * Variant/plan for the tracking payload, read fresh at click time rather
   * than off `this.lastVariant`. `product-form-update` — the event that fills
   * `lastVariant` — only fires from a variant *change*
   * (assets/product-form.js's handleVariantChange), never from initial load,
   * so a shopper who never touches a size pill would otherwise always report
   * a null variantId. `product-form`'s own getState() has no such gap.
   */
  trackingContext() {
    if (this.adapter !== 'product-form') {
      return { variant: null, productId: null, isSubscription: false, sellingPlanId: null };
    }

    const host = this.scope.closest('product-form');
    const state = host && typeof host.getState === 'function' ? host.getState() : null;

    const variant = (state && state.selectedVariant) || this.lastVariant || null;
    const isSubscription = state ? Boolean(state.isSubscription) : Boolean(this.lastIsSubscription);
    const sellingPlanId = isSubscription
      ? (state ? state.selectedSellingPlanId : this.lastSellingPlanId)
      : null;

    // Not variant.product_id: the hand-rolled JSON in product-{id}-data
    // (sections/pdp-hero-v2.liquid and its variants) never carries that key.
    // The product-form host's own data-product-id always does.
    const productId = host ? host.dataset.productId || null : null;

    return { variant, productId, isSubscription, sellingPlanId };
  }

  /* --------------------------------------------------------------- wiring */

  bindControls() {
    this.cta.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.cta.disabled) return;

      // Fired from here, not from the real control: that button is shared
      // with the buy box, so tracking its click there could not tell the two
      // entry points apart.
      const { variant, productId, isSubscription, sellingPlanId } = this.trackingContext();
      this.trackIgEvent(IG_EVENT_STICKY_ATC_CLICK, {
        adapter: this.adapter,
        variantId: variant ? variant.id : null,
        productId,
      });
      if (isSubscription) {
        this.trackIgEvent(IG_EVENT_STICKY_ATC_SUBSCRIPTION_ATTACH, {
          variantId: variant ? variant.id : null,
          productId,
          sellingPlanId,
        });
      }

      // Click the real control so its own handler runs: UpCart's interception
      // on one side, <ctr-buy-box>'s fetch to /cart/add.js on the other.
      this.control.click();
      this.close();
    });

    if (!this.hasChoices) return;

    this.toggle.addEventListener('click', (event) => {
      event.preventDefault();
      this.open();
    });

    this.querySelectorAll('[data-sticky-option]').forEach((pill) => {
      pill.addEventListener('click', (event) => {
        event.preventDefault();
        if (pill.disabled) return;
        this.selectOption(pill.dataset.stickyOption, pill.dataset.stickyValue);
      });
    });

    this.querySelectorAll('[data-sticky-plan]').forEach((plan) => {
      const row = plan.querySelector('[data-sticky-plan-select]');
      if (!row) return;
      row.addEventListener('click', (event) => {
        event.preventDefault();
        this.selectPlan(plan.dataset.stickyPlan);
      });
    });

    if (this.freqButton) {
      // Take the buy box's current frequency rather than re-deriving the
      // "which one is default" rule in two places.
      const realSelect = this.scope.querySelector('select[id$="-sellingPlanOptions"]');
      if (realSelect && realSelect.value) this.markFrequency(realSelect.value);

      this.freqButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleFrequencyList();
      });

      this.freqOptions.forEach((option) => {
        option.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.markFrequency(option.dataset.value);
          this.selectFrequency(option.dataset.value);
          this.closeFrequencyList();
          this.freqButton.focus();
        });
      });

      this.freqRoot.addEventListener('keydown', (event) => this.onFrequencyKeydown(event));
    }

    // "Tap anywhere else on the page and it closes." Capture phase so it still
    // fires for handlers that stop propagation further down. A tap outside the
    // frequency list closes that first, leaving the sheet open.
    this.onDocumentPointerDown = (event) => {
      if (this.freqRoot && !this.freqRoot.contains(event.target)) this.closeFrequencyList();
      if (!this.hasAttribute('data-open')) return;
      if (this.panel.contains(event.target)) return;
      this.close();
    };
    document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
  }

  /** Whole sheet visible, then the slide-up keyframes run off `data-open`. */
  open() {
    if (!this.hasChoices || this.hasAttribute('data-open')) return;

    this.cancelClose();
    this.body.hidden = false;
    this.cta.hidden = false;
    if (this.dividerEl) this.dividerEl.hidden = false;
    this.toggle.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'true');
    if (this.scrimEl) this.scrimEl.hidden = false;
    this.setAttribute('data-open', '');
  }

  /**
   * Slide the sheet back down, then bring the collapsed bar back up with the
   * same curve — swapping the contents at translateY(0) would pop.
   * `immediate` skips both, for when the bar is being hidden anyway.
   */
  close(immediate) {
    if (!this.hasChoices || !this.hasAttribute('data-open')) return;

    this.removeAttribute('data-open');
    if (this.scrimEl) this.scrimEl.hidden = true;

    this.closeFrequencyList();

    const collapse = () => {
      this.body.hidden = true;
      this.cta.hidden = true;
      if (this.dividerEl) this.dividerEl.hidden = true;
      this.toggle.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'false');
    };

    const reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (immediate || reduced) {
      this.removeAttribute('data-closing');
      collapse();
      return;
    }

    this.setAttribute('data-closing', '');
    this.afterSlide(() => {
      this.removeAttribute('data-closing');
      collapse();
      // Re-run the in-animation so the collapsed bar rises rather than appears.
      // `data-restoring`, not `data-open` — the latter also carries the
      // open-state paddings, which would leave the bar 14px too tall for the
      // length of the animation and drop it when the attribute came off.
      this.setAttribute('data-restoring', '');
      this.afterSlide(() => this.removeAttribute('data-restoring'));
    });
  }

  /**
   * Run `done` when the panel's current keyframe animation ends. The timeout is
   * not belt-and-braces: `animationend` never fires if the animation was never
   * applied (reduced motion, a hidden tab that never paints).
   */
  afterSlide(done) {
    const panel = this.panel;
    const finish = () => {
      panel.removeEventListener('animationend', finish);
      clearTimeout(this.slideTimer);
      this.slideTimer = null;
      done();
    };
    panel.addEventListener('animationend', finish);
    this.slideTimer = setTimeout(finish, 500);
  }

  cancelClose() {
    if (this.slideTimer) {
      clearTimeout(this.slideTimer);
      this.slideTimer = null;
    }
    this.removeAttribute('data-closing');
    this.removeAttribute('data-restoring');
  }

  /* ------------------------------------------------------------ forwarding */

  /** Click the buy box's own radio for this option value. */
  selectOption(position, value) {
    const inputs = Array.from(
      this.scope.querySelectorAll(`.variant-option-input[data-option-position="${position}"]`)
    );
    const target = inputs.find((input) => input.value === value);
    if (target && !target.disabled) target.click();
  }

  /** Click the buy box's own purchase-type radio. */
  selectPlan(type) {
    const input = this.scope.querySelector(`input[name="purchase_type"][value="${type}"]`);
    if (input && !input.disabled) input.click();
  }

  /* ------------------------------------------------------- frequency list */

  toggleFrequencyList() {
    if (this.freqList.hidden) {
      this.openFrequencyList();
    } else {
      this.closeFrequencyList();
    }
  }

  openFrequencyList() {
    this.freqList.hidden = false;
    this.freqButton.setAttribute('aria-expanded', 'true');
    const current = this.freqOptions.find((o) => o.getAttribute('aria-selected') === 'true');
    (current || this.freqOptions[0]).focus();
  }

  closeFrequencyList() {
    if (!this.freqList || this.freqList.hidden) return;
    this.freqList.hidden = true;
    this.freqButton.setAttribute('aria-expanded', 'false');
  }

  /**
   * Point the trigger at whatever plan the buy box currently holds. Prefers the
   * id the form update carried, falling back to the real select's own value.
   */
  syncFrequencyFromBuyBox(planId) {
    if (!this.freqButton) return;
    let id = planId;
    if (!id) {
      const realSelect = this.scope.querySelector('select[id$="-sellingPlanOptions"]');
      id = realSelect && realSelect.value;
    }
    if (id) this.markFrequency(id);
  }

  /** Reflect a chosen plan id in the button label and the option states. */
  markFrequency(planId) {
    const match = this.freqOptions.find((o) => o.dataset.value === String(planId));
    if (!match) return;
    this.freqOptions.forEach((o) => o.setAttribute('aria-selected', o === match ? 'true' : 'false'));
    if (this.freqValueEl) this.freqValueEl.textContent = match.textContent.trim();
  }

  /** Arrow keys, Enter/Space and Escape — what the native control gave us. */
  onFrequencyKeydown(event) {
    const { key } = event;
    if (this.freqList.hidden) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.openFrequencyList();
      }
      return;
    }

    if (key === 'Escape') {
      event.preventDefault();
      this.closeFrequencyList();
      this.freqButton.focus();
      return;
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      const focused = this.freqOptions.find((o) => o === document.activeElement);
      if (focused) {
        this.markFrequency(focused.dataset.value);
        this.selectFrequency(focused.dataset.value);
      }
      this.closeFrequencyList();
      this.freqButton.focus();
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      const index = this.freqOptions.indexOf(document.activeElement);
      const next = key === 'ArrowDown' ? index + 1 : index - 1;
      const target = this.freqOptions[(next + this.freqOptions.length) % this.freqOptions.length];
      if (target) target.focus();
    }
  }

  /** Set the buy box's frequency select and let its own listener react. */
  selectFrequency(planId) {
    const select = this.scope.querySelector('select[id$="-sellingPlanOptions"]');
    if (!select || select.value === planId) return;
    select.value = planId;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------------------------------------------------------------- reveal */

  watchScroll() {
    this.observeControl();

    // Belt and braces. The observer is the right tool and does the work in a
    // healthy foreground tab, but it is throttled to nothing in a backgrounded
    // or non-painting one — and a sticky ATC that silently never appears is the
    // whole feature gone. The scroll listener is passive and does one
    // getBoundingClientRect against state that scrolling has already computed,
    // and setVisible() only touches the DOM when the answer changes.
    this.onScroll = () => this.refreshVisibility();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
  }

  /** Both observers follow whichever element is currently the control. */
  observeControl() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Past it, not merely below it: the bar must not appear before the
          // shopper has reached the buy box on the way down.
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.setVisible(scrolledPast && isRendered(this.control));
        });
      },
      { threshold: 0 }
    );
    this.observer.observe(this.control);

    this.controlObserver = new MutationObserver(() => this.syncDisabled());
    this.controlObserver.observe(this.control, {
      attributes: true,
      attributeFilter: ['disabled', 'style', 'class', 'hidden'],
      childList: true,
      subtree: true,
    });
  }

  /**
   * Recompute from geometry. The observer only fires when the control crosses
   * the viewport edge, so a state change that happens while the shopper is
   * standing still (a flavour coming back in stock, a variant switch) needs its
   * own re-evaluation.
   */
  refreshVisibility() {
    if (!this.refreshControl()) {
      this.setVisible(false);
      return;
    }
    this.setVisible(this.control.getBoundingClientRect().bottom < 0);
  }

  setVisible(visible) {
    if (visible === this.hasAttribute('data-visible')) return;

    if (visible) {
      this.setAttribute('data-visible', 'true');
    } else {
      this.removeAttribute('data-visible');
      this.close(true);
    }
  }

  /* ----------------------------------------------------------------- state */

  watchState() {
    if (this.adapter === 'ctr') {
      this.scope.addEventListener('ctr:buy-box-update', (e) => this.renderCtr(e.detail));
      return;
    }

    const host = this.scope.closest('product-form') || document;
    host.addEventListener('product-form-update', (e) => this.renderProductForm(e.detail));

    // A variant change re-dispatches `product-form-update`; a purchase-type
    // change does not. product-form.js handles `purchase-type-change` by
    // updating its own state and stops there, so switching plan would leave the
    // sheet showing the old selection and the old price. Listen for it too.
    host.addEventListener('purchase-type-change', (e) => {
      const state = typeof host.getState === 'function' ? host.getState() : null;
      const variant = (state && state.selectedVariant) || this.lastVariant;
      if (!variant) return;

      this.renderProductForm({
        variant,
        isSubscription: e.detail.isSubscription,
        sellingPlanId: e.detail.sellingPlanId,
      });
    });
  }

  /**
   * @param {{variant: Object, isSubscription: boolean, sellingPlanId: (string|number|null)}} detail
   */
  renderProductForm(detail) {
    const variant = detail && detail.variant;
    if (!variant) return;
    this.lastVariant = variant;
    // Read by the CTA's click handler to decide whether to also fire
    // sticky_atc_subscription_attach — ctr never sets these, so that event
    // never fires for a family with no plan concept in the sheet.
    this.lastIsSubscription = Boolean(detail.isSubscription);
    this.lastSellingPlanId = detail.isSubscription ? detail.sellingPlanId : null;

    if (!variant.available) {
      this.setVisible(false);
      return;
    }

    const onetime = variant.price;
    let price = onetime;
    let compare = variant.compare_at_price > onetime ? variant.compare_at_price : null;
    let subscription = null;

    const allocation = (variant.selling_plan_allocations || []).find(
      (item) => String(item.selling_plan_id) === String(detail.sellingPlanId)
    );

    if (detail.isSubscription && allocation) {
      subscription = allocation.per_delivery_price || allocation.price;
      price = subscription;
      // The design strikes through what the shopper would otherwise pay.
      compare = allocation.compare_at_price || onetime;
    } else if ((variant.selling_plan_allocations || []).length) {
      const first = variant.selling_plan_allocations[0];
      subscription = first.per_delivery_price || first.price;
    }

    this.renderPrices(price, compare);
    this.renderChoices(variant, detail.isSubscription, onetime, subscription);
    // CNV-5212 gives each variant its own preselected frequency, so the label
    // has to follow a size change, not just the initial sync.
    this.syncFrequencyFromBuyBox(detail.isSubscription ? detail.sellingPlanId : null);
    this.syncDisabled();
    this.refreshVisibility();
  }

  /** Mark the pills and plan rows the buy box currently has selected. */
  renderChoices(variant, isSubscription, onetimeCents, subscriptionCents) {
    if (!this.hasChoices) return;

    const values = [variant.option1, variant.option2, variant.option3];
    this.querySelectorAll('[data-sticky-option]').forEach((pill) => {
      const position = Number(pill.dataset.stickyOption);
      const selected = values[position - 1] === pill.dataset.stickyValue;
      pill.toggleAttribute('data-selected', selected);
    });

    this.querySelectorAll('[data-sticky-plan]').forEach((plan) => {
      const selected = (plan.dataset.stickyPlan === 'subscription') === Boolean(isSubscription);
      plan.toggleAttribute('data-selected', selected);
    });

    if (this.onetimePriceEl) {
      this.onetimePriceEl.textContent = money(onetimeCents, this.moneyShape);
    }
    if (this.subPriceEl && Number.isFinite(subscriptionCents)) {
      this.subPriceEl.textContent = money(subscriptionCents, this.moneyShape);
    }
    if (this.subCompareEl) {
      const show = Number.isFinite(subscriptionCents) && onetimeCents > subscriptionCents;
      this.subCompareEl.textContent = show ? money(onetimeCents, this.moneyShape) : '';
      this.subCompareEl.hidden = !show;
    }
  }

  /**
   * @param {{total: number, subtotal: number, savingsPercent: (number|null),
   *          canSubmit: boolean, soldOut: boolean, label: string}} detail
   */
  renderCtr(detail) {
    if (!detail) return;

    if (detail.soldOut) {
      this.setVisible(false);
      return;
    }

    if (detail.canSubmit) {
      this.renderPrices(detail.total, detail.subtotal > detail.total ? detail.subtotal : null);
    } else {
      this.renderPrices(null, null);
    }

    this.cta.disabled = !detail.canSubmit;
    this.toggleAttribute('data-no-price', !detail.canSubmit);
    if (this.labelEl && !detail.canSubmit && detail.label) {
      this.labelEl.textContent = detail.label;
    } else if (this.labelEl) {
      this.labelEl.textContent = this.dataset.buttonText || this.labelEl.textContent;
    }

    this.refreshVisibility();
  }

  renderPrices(priceCents, compareCents) {
    if (this.priceEl) {
      this.priceEl.textContent = Number.isFinite(priceCents)
        ? money(priceCents, this.moneyShape)
        : '';
      this.priceEl.hidden = !Number.isFinite(priceCents);
      if (this.dashEl) this.dashEl.hidden = !Number.isFinite(priceCents);
    }
    if (this.compareEl) {
      const show = Number.isFinite(compareCents) && compareCents > priceCents;
      this.compareEl.textContent = show ? money(compareCents, this.moneyShape) : '';
      this.compareEl.hidden = !show;
    }
  }

  /** Mirror the real control's disabled/hidden state onto the bar. */
  syncDisabled() {
    if (!this.refreshControl()) {
      this.setVisible(false);
      return;
    }

    this.cta.disabled = Boolean(this.control.disabled);

    // The sample-stick handler rewrites the real button to "Limit One Per
    // Customer". Borrow that text rather than re-deriving the rule here.
    if (this.adapter === 'product-form' && this.labelEl) {
      const text = this.control.textContent.trim();
      this.labelEl.textContent =
        this.control.disabled && text ? text : this.dataset.buttonText || this.labelEl.textContent;
    }
  }
}

if (!customElements.get('sticky-atc-bar')) {
  customElements.define('sticky-atc-bar', StickyAtcBar);
}
